// src/pages/payment/PaymentReturnPage.jsx - UPDATED with fallback to latest payment
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/auth-context';
import Navbar from '../../components/marketing/Navbar';

const PaymentReturnPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, refreshUser } = useAuth();
  
  const [status, setStatus] = useState('checking'); // checking, success, failed, cancelled
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    checkPayment();
  }, [token, searchParams, navigate]);

  const checkPayment = async () => {
    try {
      // Try multiple methods to get payment ID
      let paymentId = null;
      
      // Method 1: URL parameters (primary)
      paymentId = searchParams.get('payment_id') || 
                 searchParams.get('paymentId') || 
                 searchParams.get('id');
      
      console.log('🔍 Payment ID from URL:', paymentId);
      
      // Method 2: localStorage (backup)
      if (!paymentId) {
        try {
          paymentId = localStorage.getItem('ilearn_latest_payment_id');
          console.log('💾 Payment ID from localStorage:', paymentId);
          
          // Clean up localStorage after use
          if (paymentId) {
            localStorage.removeItem('ilearn_latest_payment_id');
          }
        } catch (storageError) {
          console.warn('⚠️ Could not read from localStorage:', storageError);
        }
      }
      
      // Method 3: Fetch latest payment (final fallback)
      if (!paymentId) {
        console.log('🔄 No payment ID found, fetching latest payment...');
        await checkLatestPayment();
        return;
      }
      
      // We have a payment ID, check its status
      await checkPaymentStatus(paymentId);
      
    } catch (error) {
      console.error('❌ Error in checkPayment:', error);
      setStatus('failed');
      setError('Er is een fout opgetreden bij het controleren van de betaling');
    }
  };

  const checkLatestPayment = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/payments/latest`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        console.log('✅ Latest payment retrieved:', data.payment.paymentId);
        setPaymentDetails(data.payment);
        
        if (data.payment.isPaid) {
          setStatus('success');
          if (refreshUser) {
            await refreshUser();
          }
        } else if (data.payment.isFailed) {
          setStatus('failed');
        } else if (data.payment.status === 'canceled' || data.payment.status === 'cancelled') {
          setStatus('cancelled');
        } else {
          setStatus('pending');
        }
      } else {
        setStatus('failed');
        setError(data.message || 'Geen recente betaling gevonden');
      }
    } catch (error) {
      console.error('❌ Error fetching latest payment:', error);
      setStatus('failed');
      setError('Kon betaalstatus niet ophalen');
    }
  };

  const checkPaymentStatus = async (paymentId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/payments/status/${paymentId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        setPaymentDetails(data.payment);
        if (data.payment.isPaid) {
          setStatus('success');
          if (refreshUser) {
            await refreshUser();
          }
        } else if (data.payment.isFailed) {
          setStatus('failed');
        } else if (data.payment.status === 'canceled' || data.payment.status === 'cancelled') {
          setStatus('cancelled');
        } else {
          setStatus('pending');
        }
      } else {
        setStatus('failed');
        setError(data.message || 'Kon betaalstatus niet controleren');
      }
    } catch (error) {
      console.error('❌ Check payment status error:', error);
      setStatus('failed');
      setError('Er is een fout opgetreden bij het controleren van de betaling');
    }
  };

  const handleRetry = () => {
    navigate('/dashboard');
  };

  const handleGoToLesson = () => {
    if (paymentDetails && paymentDetails.lessonIds && paymentDetails.lessonIds.length > 0) {
      const lessonId = paymentDetails.lessonIds[0];
      navigate(`/les${lessonId.replace('les', '')}`);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-cream-100 font-['Lexend_Deca']">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8">
            
            {/* Checking Status */}
            {status === 'checking' && (
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
                <h2 className="text-2xl font-light text-gray-800 mb-2">
                  Betaling Controleren
                </h2>
                <p className="text-gray-600">
                  Een moment geduld, we controleren de status van je betaling...
                </p>
              </div>
            )}

            {/* Success Status */}
            {status === 'success' && (
              <div className="text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h1 className="text-3xl font-light text-purple-800 mb-4">
                  Betaling Succesvol!
                </h1>
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                  <p className="text-green-900 mb-4">
                    Je hebt nu toegang tot de training. Bedankt voor je aankoop!
                  </p>
                  
                  {paymentDetails && (
                    <div className="text-left text-sm text-green-800 space-y-2">
                      <p><strong>Betaald bedrag:</strong> €{paymentDetails.amount.toFixed(2)}</p>
                      <p><strong>Betaling ID:</strong> {paymentDetails.paymentId}</p>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <button
                    onClick={handleGoToLesson}
                    className="w-full px-8 py-4 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Begin Met Leren →
                  </button>
                  
                  <Link
                    to="/dashboard"
                    className="block w-full px-8 py-3 border-2 border-purple-600 text-purple-600 font-semibold rounded-lg hover:bg-purple-50 transition-colors text-center"
                  >
                    Terug naar Dashboard
                  </Link>
                </div>
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    Je ontvangt een bevestigingsmail met de details van je aankoop.
                  </p>
                </div>
              </div>
            )}

            {/* Failed Status */}
            {status === 'failed' && (
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h1 className="text-3xl font-light text-red-800 mb-4">
                  Betaling Mislukt
                </h1>
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                  <p className="text-red-900 mb-4">
                    {error || 'Je betaling is mislukt. Er is geen geld afgeschreven van je rekening.'}
                  </p>
                  
                  <div className="text-left text-sm text-red-800 space-y-2">
                    <p className="font-medium">Mogelijke oorzaken:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Onvoldoende saldo</li>
                      <li>Betaling geweigerd door bank</li>
                      <li>Technische fout</li>
                    </ul>
                  </div>
                </div>
                <div className="space-y-3">
                  <button
                    onClick={handleRetry}
                    className="w-full px-8 py-4 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Opnieuw Proberen
                  </button>
                  
                  <Link
                    to="/dashboard"
                    className="block w-full px-8 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors text-center"
                  >
                    Terug naar Dashboard
                  </Link>
                </div>
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    Hulp nodig?{' '}
                    <a href="#" className="text-purple-600 hover:text-purple-800 font-medium">
                      Neem contact met ons op
                    </a>
                  </p>
                </div>
              </div>
            )}

            {/* Cancelled Status */}
            {status === 'cancelled' && (
              <div className="text-center">
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h1 className="text-3xl font-light text-yellow-800 mb-4">
                  Betaling Geannuleerd
                </h1>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                  <p className="text-yellow-900">
                    Je hebt de betaling geannuleerd. Er is geen geld afgeschreven.
                  </p>
                </div>
                <div className="space-y-3">
                  <button
                    onClick={handleRetry}
                    className="w-full px-8 py-4 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Opnieuw Proberen
                  </button>
                  
                  <Link
                    to="/dashboard"
                    className="block w-full px-8 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors text-center"
                  >
                    Terug naar Dashboard
                  </Link>
                </div>
              </div>
            )}

            {/* Pending Status */}
            {status === 'pending' && (
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h1 className="text-3xl font-light text-blue-800 mb-4">
                  Betaling In Behandeling
                </h1>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  <p className="text-blue-900 mb-4">
                    Je betaling wordt nog verwerkt. Dit kan enkele minuten duren.
                  </p>
                  
                  <p className="text-sm text-blue-800">
                    Je ontvangt een e-mail zodra de betaling is voltooid.
                  </p>
                </div>
                <div className="space-y-3">
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full px-8 py-4 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Status Opnieuw Controleren
                  </button>
                  
                  <Link
                    to="/dashboard"
                    className="block w-full px-8 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors text-center"
                  >
                    Terug naar Dashboard
                  </Link>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentReturnPage;
